import React, { useState } from "react";
import { CreditCard, Smartphone, Zap, Star, Lock, CheckCircle2, Circle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/brand";
import { currentCurrency, formatR, formatZAR } from "@/lib/format";
import { cn } from "@/lib/utils";

const BASE_METHODS = [
  { key: "payfast", icon: CreditCard, label: "PayFast", sub: "Card · Instant EFT · SnapScan · Zapper" },
  { key: "wallet", icon: Smartphone, label: "Demo wallet", sub: "Test purchase — no real charge" },
];

export function pointsDiscount(points, price) {
  const maxDiscount = Math.floor((price * 0.5) / 10) * 10;
  const affordable = Math.floor(points / 100) * 10;
  const discount = Math.min(maxDiscount, affordable);
  return { discount, pointsUsed: (discount / 10) * 100 };
}

export function CheckoutSheet({ open, onClose, title, subtitle, amount, onPay, loading, points, fitpassCredits, lines, heading = "Checkout" }) {
  const hasFitpass = fitpassCredits === null || (typeof fitpassCredits === "number" && fitpassCredits > 0);
  const [method, setMethod] = useState(hasFitpass ? "fitpass" : "payfast");
  const [usePoints, setUsePoints] = useState(false);
  const { discount, pointsUsed } = pointsDiscount(points ?? 0, amount);
  const canUsePoints = (points ?? 0) >= 100 && discount > 0 && method !== "fitpass";
  const applied = canUsePoints && usePoints ? discount : 0;
  const total = method === "fitpass" ? 0 : Math.max(0, amount - applied);
  const fee = Math.round(amount * 0.15);
  const methods = hasFitpass
    ? [{ key: "fitpass", icon: Zap, label: "Fit Pass credit", sub: fitpassCredits === null ? "Unlimited plan" : `${fitpassCredits} credit${fitpassCredits === 1 ? "" : "s"} left this month` }, ...BASE_METHODS]
    : BASE_METHODS;
  const extras = (lines ?? []).reduce((a, [, v]) => a + v, 0);
  const grand = total + extras;
  const payLabel = method === "fitpass" ? "Book with Fit Pass" : grand === 0 ? "Confirm — nothing to pay" : method === "payfast" ? `Pay ${formatR(grand)} with PayFast` : `Pay ${formatR(grand)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md p-0 gap-0 rounded-xl2 overflow-hidden" data-testid="checkout-sheet">
        <div className="p-5 space-y-3 max-h-[85vh] overflow-y-auto">
          <DialogTitle asChild><h2 className="font-display text-2xl text-ink">{heading}</h2></DialogTitle>
          <div>
            <div className="font-display font-semibold text-base text-ink">{title}</div>
            <div className="font-body text-[13px] text-mutedgrey">{subtitle}</div>
          </div>

          <div className="font-body font-semibold text-[13px] text-ink pt-1">Payment method</div>
          <div className="space-y-2">
            {methods.map((m) => {
              const Icon = m.icon;
              const active = method === m.key;
              return (
                <button key={m.key} data-testid={`pay-method-${m.key}`} onClick={() => setMethod(m.key)}
                  className={cn("w-full flex items-center gap-3 p-3 rounded-md border text-left", active ? "border-brand bg-brand-soft2" : "border-line")}>
                  <Icon size={18} className="text-brand" />
                  <div className="flex-1">
                    <div className="font-body font-semibold text-sm text-ink">{m.label}</div>
                    <div className="font-body text-xs text-mutedgrey">{m.sub}</div>
                  </div>
                  {active ? <CheckCircle2 size={20} className="text-brand" /> : <Circle size={20} className="text-line" />}
                </button>
              );
            })}
          </div>

          {canUsePoints ? (
            <div className="flex items-center gap-3 p-3 rounded-md bg-brand-soft2" data-testid="checkout-points-row">
              <Star size={18} className="text-brand" />
              <div className="flex-1">
                <div className="font-body font-semibold text-sm text-ink">Use {pointsUsed} points</div>
                <div className="font-body text-xs text-mutedgrey">Save {formatR(discount)} · you have {points} pts · 100 pts = R10, max 50% off</div>
              </div>
              <Switch data-testid="checkout-points-toggle" checked={usePoints} onCheckedChange={setUsePoints} />
            </div>
          ) : null}

          <div className="bg-surface2 rounded-md p-3 space-y-2">
            <Row l={lines ? "Subtotal" : "Session"} v={formatR(amount)} />
            {(lines ?? []).map(([l, v]) => <Row key={l} l={l} v={formatR(v)} />)}
            {!lines ? <Row l="Platform fee (included, 15%)" v={formatR(fee)} /> : null}
            {applied > 0 ? <Row l="Points discount" v={`−${formatR(applied)}`} green testid="checkout-discount" /> : null}
            {method === "fitpass" ? <Row l="Fit Pass credit" v={`−${formatR(amount)}`} green /> : null}
            <div className="flex justify-between border-t border-divider pt-2">
              <span className="font-display text-base text-ink">Total</span>
              <span className="font-display text-lg text-brand" data-testid="checkout-total">{formatR(grand)}</span>
            </div>
            {currentCurrency() !== "ZAR" && grand > 0 ? (
              <div className="font-body text-[11px] text-mutedgrey text-right" data-testid="checkout-zar-note">
                Charged in South African Rand: {formatZAR(grand)} · shown in {currentCurrency()} at today's rate
              </div>
            ) : null}
          </div>

          <Button testID="checkout-pay-button" title={payLabel} icon={Lock} loading={loading} full onClick={() => onPay(method, canUsePoints && usePoints)} />
          <p className="font-body text-[11px] text-mutedgrey text-center">
            {method === "payfast" ? "You'll be taken to PayFast's secure checkout, then returned to Sofit." : method === "fitpass" ? "One credit will be used from your monthly Fit Pass." : "Demo wallet books instantly for testing purposes."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Row({ l, v, green, testid }) {
  return (
    <div className="flex justify-between">
      <span className={cn("font-body text-[13px]", green ? "text-success" : "text-mutedgrey")}>{l}</span>
      <span className={cn("font-body font-semibold text-[13px]", green ? "text-success" : "text-ink")} data-testid={testid}>{v}</span>
    </div>
  );
}
